import * as React from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { SingleSelect } from '@/modules/common';
import { ExternalEntity } from '@/modules/common/types/entity-integration';
import { TeamMemberIntegration } from '@/modules/team';

interface TeamMemberIntegrationsSelectCellProps {
  integration: TeamMemberIntegration;
  onTeamMemberChange: (integrationId: string, memberId?: string) => void;
  isReadMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

const NOT_SELECTED_VALUE = '__not_selected__';

export const TeamMemberIntegrationsSelectCell: React.FC<TeamMemberIntegrationsSelectCellProps> = ({
  integration,
  onTeamMemberChange,
  isReadMode,
  t,
}) => {
  const form = useForm({
    defaultValues: {
      selectedMemberId: integration.selectedValue?.id || NOT_SELECTED_VALUE,
    },
  });

  // Watch form changes
  const selectedMemberId = form.watch('selectedMemberId');

  // Update form when integration changes externally (from parent state updates)
  React.useEffect(() => {
    form.setValue('selectedMemberId', integration.selectedValue?.id || NOT_SELECTED_VALUE);
  }, [integration.selectedValue?.id, form]);

  // Trigger callback when user makes a selection
  React.useEffect(() => {
    // Only trigger if the current form value differs from the integration's value
    // This means user made a change that hasn't been reflected in parent state yet
    const currentValue = integration.selectedValue?.id || NOT_SELECTED_VALUE;
    if (selectedMemberId !== currentValue) {
      // Convert special "not selected" value to undefined
      const memberId = selectedMemberId === NOT_SELECTED_VALUE ? undefined : selectedMemberId;
      onTeamMemberChange(integration.id, memberId);
    }
  }, [selectedMemberId, integration.id, integration.selectedValue?.id, onTeamMemberChange]);

  // Build options with "Not selected" as first option
  const memberOptions = React.useMemo(() => {
    const notSelectedOption = {
      value: NOT_SELECTED_VALUE,
      label: t('details.tabs.integrations.notSelected'),
    };

    const mappedMembers = (integration.teamMembers || []).map((member: ExternalEntity) => ({
      value: member.id,
      label: member.name,
    }));

    return [notSelectedOption, ...mappedMembers];
  }, [integration.teamMembers, t]);

  const isDisabled = isReadMode || (integration.teamMembers?.length || 0) === 0;

  const selectElement = (
    <div className="w-full max-w-xs">
      <Form {...form}>
        <SingleSelect
          name="selectedMemberId"
          control={form.control}
          placeholder={
            integration.allMembersLinked
              ? t('details.tabs.integrations.notSelected')
              : (integration.teamMembers?.length || 0) === 0
                ? t('details.tabs.integrations.noMembersAvailable')
                : t('details.tabs.integrations.selectMember')
          }
          options={memberOptions}
          disabled={isDisabled}
          searchable={true}
          showDescriptionInOptions={false}
          showDescriptionInSelector={false}
        />
      </Form>
    </div>
  );

  if (integration.allMembersLinked) {
    return (
      <SimpleTooltip
        content={t('details.tabs.integrations.allMembersLinkedTooltip', {
          integrationName: integration.type,
        })}
      >
        {selectElement}
      </SimpleTooltip>
    );
  }

  return selectElement;
};
